<template>
  <div>
    <media-uploader
      ref="mediaUploader"
      v-slot:default="slotProps"
      @uploads="updateUploads"
      @valid="emitValid"
      title-override="FOTOS HOCHLADEN"
      :only-images="true"
      :max-uploads="999"
      :validate-file-before-upload="validateFileBeforeUpload"
    >
      <GalleryUploaderGrid
        v-if="galleryUploads.length > 0 || existingGalleryMedia.length > 0"
        class="mt-4 mb-4"
      >
        <GalleryThumbnail
          v-for="medium in existingGalleryMedia"
          :key="medium.id"
          :image="medium.uploaded_file.thumb.url"
          :alt="medium.uploaded_file.file_name"
          :action-bar="buildActionBar(emitDeleteExisting, medium)"
        />
        <GalleryThumbnail
          v-for="upload in galleryUploads"
          :key="upload.file.token"
          :image="upload.thumbDataUrl"
          :alt="upload.name"
          :loading="upload.uploadProgress < 100"
          :action-bar="
            buildActionBar(slotProps.removeUpload, upload.file.token)
          "
          :error="
            upload.errors.length > 0
              ? {
                  message: upload.errors[0],
                  action: {
                    action: () => {
                      retryUpload(upload.file.token)
                    },
                    icon: mdiRefresh,
                  },
                }
              : null
          "
        />
      </GalleryUploaderGrid>
    </media-uploader>
    <v-snackbar
      @input="closeSnackbar(!$event)"
      :multi-line="true"
      v-model="showSnackbar"
      :timeout="-1"
    >
      <template v-slot:action="{ attrs }">
        <div style="padding-top: 16px; padding-bottom: 16px">
          <span v-html="errorHtml" />
        </div>
        <v-btn
          text
          v-bind="attrs"
          @click="closeSnackbar"
          color="primary"
          class="text-white"
        >
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, PropType, Ref, ref } from "vue"
import { GalleryMedium } from "features/gallery/gallery.types"
import { MediaUpload } from "features/media/media.types"
import MediaUploader from "features/media/MediaUploader.vue"
import GalleryThumbnail, {
  GalleryThumbnailAction,
} from "features/gallery/thumbnail/GalleryThumbnail.vue"
import GalleryUploaderGrid from "features/gallery/uploader/GalleryUploaderGrid.vue"
import { mdiRefresh, mdiTrashCanOutline } from "@mdi/js"
import { ConfirmationService } from "features/common/confirmation-dialog/confirmation.service"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { EventMedium } from "suus-api"
import {
  getErrorMessageHtml,
  isFileSupported,
} from "app/modules/core/supported-image-formats"

export default defineComponent({
  name: "GalleryUploader",
  components: { GalleryUploaderGrid, GalleryThumbnail, MediaUploader },
  props: {
    existingGalleryMedia: {
      type: Array as PropType<GalleryMedium[]>,
      default() {
        return []
      },
    },
  },
  setup(props, context) {
    const confirmationService = inject<ConfirmationService>(
      "ConfirmationService"
    )
    const galleryUploads: Ref<MediaUpload[]> = ref([])
    const mediaUploader = ref(null)

    const emitUploads = () => {
      context.emit("uploads", galleryUploads.value)
    }

    const emitValid = (valid: boolean) => {
      context.emit("valid", valid)
    }

    const emitDeleteExisting = (medium: EventMedium | string) => {
      if (typeof medium === "string") {
        context.emit("deleteExisting", medium)
      } else {
        context.emit("deleteExisting", medium.id)
      }
    }

    const updateUploads = (uploads: MediaUpload[]) => {
      galleryUploads.value = uploads
      emitUploads()
    }

    const { t$ } = useTranslate(
      "components.gallery_uploader.delete_confirmation"
    )

    const buildActionBar = (
      deleteUploadFunction: (input: string | GalleryMedium) => void,
      entity: string | GalleryMedium
    ): GalleryThumbnailAction[] => {
      return [
        {
          action: () => {
            confirmationService
              .open({
                title: t$(".title"),
                text: t$(".text"),
                confirmText: t$(".delete"),
              })
              .then((confirmed: boolean) => {
                console.log("confirmed", confirmed)
                if (confirmed) {
                  deleteUploadFunction(entity)
                }
              })
          },
          icon: mdiTrashCanOutline,
        },
      ]
    }

    const retryUpload = (token: string) => {
      if (token) {
        console.log("retryUpload", mediaUploader.value)
        mediaUploader.value.retry(token)
      }
    }

    const wrongFormatFiles = ref<File[]>([])
    const validateFileBeforeUpload = (file: File) => {
      const isSupported = isFileSupported(file)
      if (!isSupported) {
        wrongFormatFiles.value = [...wrongFormatFiles.value, file]

        if (!showSnackbar.value) {
          openSnackbar()
        }
        return false
      }
      return true
    }
    const errorHtml = computed(() => {
      return getErrorMessageHtml(wrongFormatFiles.value)
    })
    const showSnackbar = ref(false)

    const openSnackbar = () => {
      showSnackbar.value = true
    }
    const closeSnackbar = (shouldClose = true) => {
      if (!shouldClose) return
      showSnackbar.value = false
      // Timeout removes flickering between different error messages
      setTimeout(() => {
        wrongFormatFiles.value = []
      }, 300)
    }

    return {
      emitDeleteExisting,
      emitValid,
      galleryUploads,
      updateUploads,
      buildActionBar,
      mediaUploader,
      retryUpload,
      mdiRefresh,
      validateFileBeforeUpload,
      errorHtml,
      showSnackbar,
      closeSnackbar,
    }
  },
})
</script>
